import { GridApi, GridRowId } from "@mui/x-data-grid";
import { Subject } from "rxjs";
import { isSqlFirewallView, View, ViewID } from "../model/qubes/qubes-model";
import { BiqlFilter, TableModel } from "../components/qubes-table/table-model";

type ViewDependencyWrapper = {
  dependentViewId: ViewID;
  filter: BiqlFilter;
};

type LastViewState = {
  rowIds: GridRowId[];
  columnField?: string;
};

type ViewRegistration = {
  view: View;
  api: React.MutableRefObject<GridApi | null>;
  tableModel: TableModel<Record<string, unknown>>;
};

/**
 * Service to manage view dependencies in Qubes. The ViewDependencyManager class
 * listens for changes in views and notifies dependent views to update their filters
 * accordingly. The class itself is a singleton to ensure a single source of truth
 * for view dependencies.
 */
export class ViewManager {
  /**
   * The singleton instance of the ViewManager.
   */
  private static _instance: ViewManager;

  /**
   * Map of view IDs to their registered dependencies.
   */
  private dependencies: Map<ViewID, ViewDependencyWrapper[]> = new Map();
  /**
   * Map of view IDs to their registered views and associated Grid APIs.
   */
  private views: Map<ViewID, ViewRegistration> = new Map();

  /**
   * Map of view IDs to their last known state (selected row IDs and column field).
   */
  private lastViewStates: Map<ViewID, LastViewState> = new Map();

  /**
   * Fires (a view id) whenever ANY view's selection changes. Views subscribe
   * to re-render selection-derived UI (drill-down scope titles / empty-state
   * messages) even when their own row set doesn't change — e.g. a panel that
   * stays empty after selecting a parent with no children still needs its
   * title/message to update.
   */
  readonly selectionChanged = new Subject<ViewID>();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static get(): ViewManager {
    if (!ViewManager._instance) {
      ViewManager._instance = new ViewManager();
    }
    return ViewManager._instance;
  }

  /**
   * Resets the manager by clearing all registered dependencies and views.
   */
  public reset() {
    this.dependencies.clear();
    this.views.clear();
  }

  /**
   * Registers a new view in the manager.
   * @param {BiqlView} view The view to register.
   * @param {GridApi} api The Grid API for the view.
   */
  public registerView(
    view: View,
    api: React.MutableRefObject<GridApi | null>,
    tableModel: TableModel<Record<string, unknown>>
  ) {
    this.views.set(view.id, { view, api, tableModel });
    // A supplier that (re)mounts AFTER its dependents registered — a form view
    // under React StrictMode's double effect, any panel remounted — had its
    // dependents' entries dropped by deregisterView; put them back, or the
    // dependents never hear its selection again
    // (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter).
    for (const [dependentId, reg] of this.views) {
      if (dependentId === view.id || !isSqlFirewallView(reg.view)) continue;
      for (const f of reg.view.filters ?? []) {
        if (f.dependency?.dependsOn !== view.id) continue;
        const have = this.dependencies.get(view.id) ?? [];
        if (!have.some((d) => d.filter === f)) this._registerDependency(dependentId, f);
      }
    }
    // Register this view's filter dependencies. Views are sql-firewall now;
    // gating on isBiqlView (a type no longer used) meant NO view ever registered
    // its dependencies, so selecting a row never refreshed the dependent view
    // (e.g. selecting a schedule never loaded its events).
    if (isSqlFirewallView(view) && view.filters) {
      view.filters.forEach((filter) => {
        if (filter.dependency) {
          this._registerDependency(view.id, filter);

          // Initialize the filter state based on the current state of the dependency
          const lastDependencyState = this.lastViewStates.get(
            filter.dependency.dependsOn
          );
          if (lastDependencyState)
            this._notifyDependencies(
              view.id,
              lastDependencyState.rowIds,
              lastDependencyState.columnField
            );
        }
      });
    }

  }

  /**
   * The selected row of a view, or undefined when nothing — or more than one
   * thing — is selected. The page DSL's `sdk.lineage(viewId)` reads this
   * (FEATURE-20260824-firewall-as-external-page); it is the selection state
   * the drill-down already tracks, exposed instead of mirrored elsewhere.
   */
  public selectedRow(viewId: ViewID): Record<string, unknown> | undefined {
    const state = this.lastViewStates.get(viewId);
    if (!state || state.rowIds.length !== 1) return undefined;
    const row = this.views.get(viewId)?.tableModel.currentRows.get(String(state.rowIds[0]));
    return row === undefined || row === null ? undefined : (row as Record<string, unknown>);
  }

  /** Re-fetch one view with its current filters (the page DSL's `sdk.refresh`). */
  public refreshView(viewId: ViewID) {
    const entry = this.views.get(viewId);
    if (!entry) return;
    const filters = isSqlFirewallView(entry.view) ? entry.view.filters : undefined;
    entry.tableModel.refresh?.(filters ?? []);
  }

  /**
   * Deregisters a view from the manager.
   * @param {ViewID} viewId The ID of the view to deregister.
   */
  public deregisterView(viewId: ViewID) {
    // Remove the view from the registered views
    this.views.delete(viewId);

    // Also remove any dependencies associated with this view.
    // TODO: If we re-add this view, how will we restore its dependencies?
    this.dependencies.delete(viewId);

    // Remove our dependencies from other views
    this.dependencies.forEach((deps, parentViewId) => {
      const filteredDeps = deps.filter((dep) => dep.dependentViewId !== viewId);
      this.dependencies.set(parentViewId, filteredDeps);
    });
  }

  /**
   * Notifies the manager that the row selection has changed for a given view.
   * @param {ViewID} viewId The ID of the view.
   * @param rowIds The selected row IDs.
   * @param {string} columnField The name of the column field that triggered the selection change.
   */
  public notifyRowSelectionChanged(
    viewId: ViewID,
    rowIds: GridRowId[],
    columnField?: string
  ) {
    this.lastViewStates.set(viewId, { rowIds, columnField });
    this._notifyDependencies(viewId, rowIds, columnField);
    this.selectionChanged.next(viewId);
  }

  /**
   * Registers a dependency for a given view.
   * @param {ViewID} viewId The ID of the view that has dependencies.
   * @param {BiqlFilter} filter The filter to register.
   */
  private _registerDependency(viewId: ViewID, filter: BiqlFilter) {
    const dependencyViewId = filter.dependency?.dependsOn;
    if (!dependencyViewId) return;
    // Initialize the dependencies array for the view if it doesn't exist
    if (!this.dependencies.has(dependencyViewId)) {
      this.dependencies.set(dependencyViewId, []);
    }
    // Add the dependency to the view's dependencies
    this.dependencies
      .get(dependencyViewId)!
      .push({ dependentViewId: viewId, filter });
  }

  /**
   * Notifies dependent views when a view's state changes.
   * @param {ViewID} viewIdThatChanged The ID of the view that changed.
   * @param rowIds The selected row IDs that triggered the change.
   * @param {string} columnField The name of the column field that triggered the change.
   */
  private _notifyDependencies(
    viewIdThatChanged: ViewID,
    rowIds: GridRowId[],
    columnField?: string
  ) {
    // Get the dependencies for the changed view
    const dependencies: ViewDependencyWrapper[] | undefined =
      this.dependencies.get(viewIdThatChanged);
    if (!dependencies) return; // No dependencies to notify

    // Iterate over any dependencies that depend on the changed view
    dependencies.forEach((depWrapper) => {
      const dependentApi = this.views.get(depWrapper.dependentViewId)?.api.current;
      const sourceApi = this.views.get(viewIdThatChanged)?.api.current;
      if (!dependentApi || !sourceApi) return;

      // Notify the dependency of the selection change
      depWrapper.filter?.dependency?.onDependencySelectionChanged(
        depWrapper.filter,
        dependentApi, // api of the dependent view
        sourceApi, // api of the view that changed
        rowIds,
        columnField
      );
    });

    // For each dependency, we now need to assess if the filter was satisfied, and if
    // all the filters are satisfied, we need to refresh the view. NOTE: We cannot perform
    // this logic inside the loop above, because we need to ensure all dependencies have
    // been updated first.

    // step one is to determine all dependent views that need refreshing
    const viewsToRefresh: Set<ViewID> = new Set();
    dependencies.forEach((depWrapper) => {
      viewsToRefresh.add(depWrapper.dependentViewId);
    });
    // step two is to now refresh each dependent view if all its dependencies are satisfied
    viewsToRefresh.forEach((dependentViewId) => {
      const dependentViewEntry = this.views.get(dependentViewId);
      if (!dependentViewEntry) return;
      const dependentView = dependentViewEntry.view;
      if (!isSqlFirewallView(dependentView) || !dependentView.filters) return;

      // Determine if all dependencies are satisfied
      const allDependenciesSatisfied = dependentView.filters.every((filter) =>
        filter.isSatisfied()
      );

      if (allDependenciesSatisfied) {
        // All dependencies satisfied, refresh the view
        dependentViewEntry.tableModel.refresh?.(dependentView.filters);

        // Also clear the last known state for the dependent view since it has changed
        // and the previous row/column selection may no longer be valid.
        this.lastViewStates.delete(dependentViewId);
      } else {
        // The parent was deselected (a pick cleared, a row unselected): the rows the
        // dependent showed were THAT parent's, so they go, and the view is gated again
        // (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter). Its own
        // dependents hear the emptying through the same path.
        // refresh() behind a closed gate fetches nothing and empties the rows (table-model gateOpen).
        dependentViewEntry.tableModel.refresh?.(dependentView.filters);
        if (this.lastViewStates.has(dependentViewId)) {
          this.notifyRowSelectionChanged(dependentViewId, []);
          this.lastViewStates.delete(dependentViewId);
        }
      }
    });
  }
}
